import { Link, useNavigate } from 'react-router-dom'
import { useUnifiedCourses } from '@/lib/hooks/use-unified-courses'
import { BookOpen, Clock, PlayCircle, CheckCircle2, GraduationCap, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AnimatedLoader } from '@/components/ui/animated-loader'
import { SlideIn, FadeIn, StaggerContainer, StaggerItem } from '@/components/motion'

export default function CoursesPage() {
  const navigate = useNavigate()
  const { courses, isLoading, enroll, isEnrolling } = useUnifiedCourses()

  const handleEnroll = async (courseId: string, courseSlug: string) => {
    if (enroll) {
      enroll(courseId)
      navigate(`/genius/course/${courseSlug}`)
    }
  }

  // Show loading only if we have no cached data
  if (isLoading && !courses) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <AnimatedLoader message="Loading courses..." />
      </div>
    )
  }

  const enrolledCourses = courses?.filter(course => course.enrollment) || []

  // Calculate completion stats
  const completedCourses = enrolledCourses.filter(c => c.enrollment?.status === 'completed').length
  const totalCourses = courses?.length || 0
  const allCompleted = completedCourses >= totalCourses && totalCourses > 0
  const progressPercent = totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0

  return (
    <div className="min-h-screen bg-slate-50 pb-12 sm:pb-16">
      {/* Hero Banner */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
        <SlideIn direction="down">
          <div
            className="rounded-2xl sm:rounded-3xl lg:rounded-[32px] p-6 sm:p-8 lg:p-10 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)',
            }}
          >
            {/* Decorative circles - hidden on mobile */}
            <div
              className="absolute -right-[80px] -top-[80px] w-[300px] h-[300px] rounded-full hidden sm:block"
              style={{
                background: 'radial-gradient(closest-side, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 100%)',
              }}
            />
            <div
              className="absolute right-[100px] top-[20px] w-[150px] h-[150px] rounded-full hidden sm:block"
              style={{
                background: 'radial-gradient(closest-side, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
              }}
            />

            <div className="relative z-10">
              {/* Date line */}
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300" />
                <span className="text-slate-300 text-xs sm:text-sm font-medium">Expert Training Program</span>
              </div>

              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3">
                Level Up Your AI Expertise
              </h1>
              <p className="text-base sm:text-lg text-slate-300 max-w-[600px]">
                Complete certifications to boost your credibility and match rate by up to 40%.
              </p>
            </div>
          </div>
        </SlideIn>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mt-4 sm:mt-6">
        {/* Training Incomplete Banner */}
        {!allCompleted && totalCourses > 0 && (
          <FadeIn delay={0.2}>
            <div
              className="rounded-2xl sm:rounded-3xl lg:rounded-[40px] border-[3px] border-[rgba(245,158,11,0.60)] p-5 sm:p-6 lg:p-8 mb-6 sm:mb-8 text-center"
              style={{
                background: 'linear-gradient(99.87deg, rgba(245, 158, 11, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)',
              }}
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full mx-auto flex items-center justify-center mb-3 sm:mb-4">
                <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500" />
              </div>
              <h2 className="text-xl sm:text-2xl lg:text-[32px] font-normal text-slate-900 tracking-tight mb-2">
                Complete your training
              </h2>
              <p className="text-base sm:text-lg text-slate-500 max-w-[800px] mx-auto">
                Your training is {progressPercent}% complete. Finish all courses to unlock your certification and start receiving client opportunities.
              </p>
            </div>
          </FadeIn>
        )}

        {/* Course Grid */}
        <StaggerContainer staggerDelay={0.1} delayChildren={0.2} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {courses?.map((course) => {
            const isEnrolled = !!course.enrollment
            const isCompleted = course.enrollment?.status === 'completed'
            const progress = course.enrollment?.progress_percent || 0

            return (
              <StaggerItem key={course.id}>
                <div
                  className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[32px] border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                >
                {/* Thumbnail */}
                {course.thumbnail_url ? (
                  <img
                    src={course.thumbnail_url}
                    alt={course.title}
                    className="w-full h-40 sm:h-48 object-cover"
                  />
                ) : (
                  <div
                    className="w-full h-40 sm:h-48 flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(70, 105, 237, 0.1) 0%, rgba(54, 84, 233, 0.2) 100%)',
                    }}
                  >
                    <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-[#4669ed]/40" />
                  </div>
                )}

                {/* Content */}
                <div className="p-4 sm:p-6">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {course.title}
                  </h3>
                  {course.description && (
                    <p className="text-sm text-slate-500 line-clamp-2 mb-4">
                      {course.description}
                    </p>
                  )}

                  {/* Status & Progress */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <span
                        className={cn(
                          "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium",
                          isCompleted
                            ? "bg-emerald-100 text-emerald-700"
                            : isEnrolled
                              ? "bg-[rgba(70,105,237,0.10)] text-[#4669ed]"
                              : "bg-slate-100 text-slate-500"
                        )}
                      >
                        {isCompleted ? (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Completed
                          </>
                        ) : isEnrolled ? (
                          <>
                            <PlayCircle className="w-3.5 h-3.5" />
                            In Progress
                          </>
                        ) : (
                          <>
                            <Clock className="w-3.5 h-3.5" />
                            Start anytime
                          </>
                        )}
                      </span>
                      {isEnrolled && (
                        <span className="text-sm text-slate-500">{progress}% complete</span>
                      )}
                    </div>
                    {isEnrolled && (
                      <div className="bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div
                          className={cn(
                            "h-full transition-all duration-500",
                            isCompleted ? "bg-emerald-500" : "bg-[#4669ed]"
                          )}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  {isEnrolled ? (
                    <Link
                      to={`/genius/course/${course.slug}`}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-medium text-sm"
                      style={{
                        background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                      }}
                    >
                      {isCompleted ? 'Review Course' : 'Continue Learning'}
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleEnroll(course.id, course.slug)}
                      disabled={isEnrolling}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-medium text-sm disabled:opacity-50"
                      style={{
                        background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                      }}
                    >
                      {isEnrolling ? 'Enrolling...' : 'Enroll Now'}
                    </button>
                  )}
                </div>
                </div>
              </StaggerItem>
            )
          })}
        </StaggerContainer>

        {/* Empty State */}
        {(!courses || courses.length === 0) && (
          <FadeIn>
            <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-8 sm:p-12 text-center">
              <BookOpen className="h-12 w-12 sm:h-16 sm:w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">No courses available</h3>
              <p className="text-sm sm:text-base text-slate-500">
                Check back later for new courses to enhance your AI expertise.
              </p>
            </div>
          </FadeIn>
        )}
      </div>
    </div>
  )
}
